import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  Sparkles,
  Calendar,
  Layers,
  Boxes,
  Building2,
  Maximize2,
  BookOpen,
  GlassWater,
  Frame,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile à Châteaufort ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Livraison à domicile de vos cartons de déménagement {city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Nous savons que le temps est particulièrement précieux
              durant les semaines qui précèdent le grand jour. C&apos;est
              pourquoi notre service de livraison de proximité se déplace
              directement à votre adresse pour vous ravitailler en
              matériel à plat, vous évitant ainsi la corvée de charger
              des paquets encombrants dans votre voiture.
            </p>
            <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Nous desservons avec la plus grande réactivité
              l&apos;ensemble des habitations et des zones d&apos;activités
              {" "}{city.aCity}, ainsi que les communes voisines comme
              Magny-les-Hameaux, Toussus-le-Noble, Buc et Guyancourt.
              Vous planifiez la réception de vos cartons selon vos propres
              disponibilités, ce qui vous permet de commencer
              l&apos;emballage de vos pièces immédiatement et à votre
              rythme.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Service de proximité",
                body: "Châteaufort, Magny-les-Hameaux, Toussus-le-Noble, Buc, Guyancourt et alentours.",
                Icon: Truck,
              },
              {
                title: "Créneau à votre rythme",
                body: "Vous choisissez l'heure de réception pour démarrer l'emballage sans perdre une minute.",
                Icon: Calendar,
              },
              {
                title: "Matériel propre & sec",
                body: "Cartons à plat, sous protection, parfaitement prêts pour un pliage à haute résistance.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Formats adaptés aux appartements ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Formats appartement
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Des formats de cartons parfaitement adaptés aux appartements
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Déménager un appartement présente des contraintes logistiques
            bien spécifiques, notamment la gestion de l&apos;espace lors
            de la mise en cartons et les allers-retours dans les couloirs
            ou les ascenseurs.
          </p>
          <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Notre gamme comprend des formats compacts et standardisés qui
            optimisent chaque mètre carré de votre logement pendant la
            préparation. Ces dimensions étudiées permettent
            d&apos;empiler les boîtes de façon géométrique et stable, ce
            qui sécurise le stockage dans vos pièces et maximise le
            volume disponible à l&apos;intérieur du camion de
            déménagement. De plus, nos cartons renforcés spécial livres
            vous évitent de charger des colis trop lourds, facilitant
            grandement la manutention dans les escaliers.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Formats standardisés",
              body: "Des dimensions étudiées pour s'empiler de façon géométrique et stable, dans vos pièces comme dans le camion.",
              Icon: Maximize2,
            },
            {
              title: "Optimisation du volume",
              body: "Chaque mètre carré de votre appartement reste utilisable pendant la préparation grâce à des formats compacts.",
              Icon: Building2,
            },
            {
              title: "Spécial livres allégé",
              body: "Notre carton renforcé livres limite le poids pour faciliter le portage dans les couloirs et les escaliers.",
              Icon: BookOpen,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-chateaufort-cta-formats"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les formats disponibles
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Emballages spécifiques objets de valeur ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Protection des objets de valeur
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Des emballages spécifiques pour la protection de vos objets de
            valeur
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Chaque habitation renferme des biens précieux, qu&apos;il
            s&apos;agisse de vaisselle fine, d&apos;œuvres d&apos;art, de
            cadres familiaux ou d&apos;appareils électroniques délicats.
          </p>
          <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour ces éléments sensibles, nous mettons à votre disposition
            des solutions de protection de qualité industrielle qui
            absorbent les vibrations de la route et les chocs liés aux
            manipulations. Nos cartons double cannelure à haute
            résistance offrent une barrière solide contre les pressions
            extérieures, tandis que nos rouleaux de papier bulle et nos
            films étirables immobilisent parfaitement le contenu. En
            investissant dans du matériel adapté, vous abordez le
            transport avec une sérénité totale, sachant que vos pièces
            les plus fragiles arriveront intactes à votre nouvelle
            adresse.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Double cannelure haute résistance",
              body: "Une barrière solide contre les pressions extérieures, idéale pour la vaisselle fine et la verrerie.",
              Icon: ShieldCheck,
            },
            {
              title: "Papier bulle & film étirable",
              body: "Absorbent les vibrations de la route et immobilisent parfaitement le contenu de chaque carton.",
              Icon: Layers,
            },
            {
              title: "Cadres & œuvres d'art",
              body: "Des protections renforcées pour vos cadres familiaux, miroirs et œuvres encadrées, sans rayures ni chocs.",
              Icon: Frame,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-chateaufort-cta-protection"
          >
            <GlassWater className="h-4 w-4" strokeWidth={2.6} />
            Découvrir nos protections
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-chateaufort-cta-shop"
          >
            <Sparkles className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les cartons
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-chateaufort-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
